import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useChannel } from "@ui";
import { conversationKeys } from "@agentic_conversation/api/conversation";
import type { Conversation, Message } from "@agentic_conversation/pages/chat/types";

interface ThrottleState {
  retryAfter: number;
  startedAt: number;
}

export interface DailyUsageStatus {
  total_tokens: number;
  limit: number;
  remaining: number;
  percentage: number;
  is_warning: boolean;
  is_exceeded: boolean;
  resets_at: string;
}

interface ChatBroadcast {
  type: "message_created" | "message_updated" | "conversation_update" | "throttle_started" | "throttle_cleared" | "daily_limit_warning" | "daily_limit_reached";
  message?: Message;
  conversation?: Partial<Conversation>;
  retry_after?: number;
  reason?: string;
  usage?: DailyUsageStatus;
}

export function useChatSocket(conversationId: number | null) {
  const queryClient = useQueryClient();
  const [throttle, setThrottle] = useState<ThrottleState | null>(null);
  const [dailyUsage, setDailyUsage] = useState<DailyUsageStatus | null>(null);

  const handleReceived = useCallback(
    (data: ChatBroadcast) => {
      if (!conversationId) return;

      const queryKey = conversationKeys.detail(conversationId);

      if (data.type === "message_created" && data.message) {
        queryClient.setQueryData<Conversation>(queryKey, (old) => {
          if (!old) return old;
          if (old.messages.some((m) => m.id === data.message!.id)) return old;
          return {
            ...old,
            messages: [...old.messages, data.message!],
          };
        });
      }

      if (data.type === "message_updated" && data.message) {
        queryClient.setQueryData<Conversation>(queryKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((m) =>
              m.id === data.message!.id ? data.message! : m
            ),
          };
        });
      }

      if (data.type === "conversation_update" && data.conversation) {
        queryClient.setQueryData<Conversation>(queryKey, (old) => {
          if (!old) return old;
          return { ...old, ...data.conversation };
        });
      }

      if (data.type === "throttle_started" && data.retry_after) {
        setThrottle({
          retryAfter: data.retry_after,
          startedAt: Date.now(),
        });
      }

      if (data.type === "throttle_cleared") {
        setThrottle(null);
      }

      if (data.type === "daily_limit_warning" || data.type === "daily_limit_reached") {
        if (data.usage) {
          setDailyUsage(data.usage);
        }
      }
    },
    [conversationId, queryClient]
  );

  useChannel<ChatBroadcast>({
    params: conversationId
      ? { channel: "AgenticConversation::ChatChannel", conversation_id: conversationId }
      : null,
    onReceived: handleReceived,
  });

  return { throttle, dailyUsage };
}
